import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'psar',
  name: 'Parabolic SAR',
  shortName: 'PSAR',
  category: 'trend',
  overlay: true,
  params: [
    {
      key: 'start',
      label: 'Start AF',
      type: 'number',
      default: 0.02,
      min: 0.001,
      max: 0.5,
      step: 0.001,
    },
    {
      key: 'increment',
      label: 'AF Step',
      type: 'number',
      default: 0.02,
      min: 0.001,
      max: 0.5,
      step: 0.001,
    },
    {
      key: 'maximum',
      label: 'Max AF',
      type: 'number',
      default: 0.2,
      min: 0.01,
      max: 1,
      step: 0.01,
    },
  ],
  lines: [
    // renderStyle:'dots' → ChartCanvas renders pointMarkersVisible:true, lineVisible:false
    { key: 'bull', label: 'Bull SAR', color: '#26a69a', width: 1, renderStyle: 'dots' },
    { key: 'bear', label: 'Bear SAR', color: '#ef5350', width: 1, renderStyle: 'dots' },
  ],
}

class ParabolicSAR extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const initialAF = Math.max(0.001, Number(params['start']) || 0.02)
    const stepAF = Math.max(0.001, Number(params['increment']) || 0.02)
    const maxAF = Math.max(initialAF, Number(params['maximum']) || 0.2)

    const n = candles.length
    const bull: (number | null)[] = new Array<number | null>(n).fill(null)
    const bear: (number | null)[] = new Array<number | null>(n).fill(null)

    if (n < 2)
      return {
        lines: [
          { key: 'bull', values: bull },
          { key: 'bear', values: bear },
        ],
      }

    // Determine initial trend: bullish if first close > first open
    let trend = candles[0].c >= candles[0].o ? 1 : -1
    let sar = trend === 1 ? candles[0].l : candles[0].h
    let ep = trend === 1 ? candles[0].h : candles[0].l
    let af = initialAF

    // Seed first bar
    if (trend === 1) bull[0] = sar
    else bear[0] = sar

    for (let i = 1; i < n; i++) {
      // Step SAR
      let newSAR = sar + af * (ep - sar)

      if (trend === 1) {
        // Bullish: SAR must be below the two prior lows
        const minPrior = i >= 2 ? Math.min(candles[i - 1].l, candles[i - 2].l) : candles[i - 1].l
        newSAR = Math.min(newSAR, minPrior)

        if (candles[i].l < newSAR) {
          // Reversal to bearish
          trend = -1
          sar = ep
          ep = candles[i].l
          af = initialAF
          bear[i] = sar
        } else {
          sar = newSAR
          if (candles[i].h > ep) {
            ep = candles[i].h
            af = Math.min(af + stepAF, maxAF)
          }
          bull[i] = sar
        }
      } else {
        // Bearish: SAR must be above the two prior highs
        const maxPrior = i >= 2 ? Math.max(candles[i - 1].h, candles[i - 2].h) : candles[i - 1].h
        newSAR = Math.max(newSAR, maxPrior)

        if (candles[i].h > newSAR) {
          // Reversal to bullish
          trend = 1
          sar = ep
          ep = candles[i].h
          af = initialAF
          bull[i] = sar
        } else {
          sar = newSAR
          if (candles[i].l < ep) {
            ep = candles[i].l
            af = Math.min(af + stepAF, maxAF)
          }
          bear[i] = sar
        }
      }
    }

    return {
      lines: [
        { key: 'bull', values: bull },
        { key: 'bear', values: bear },
      ],
    }
  }
}

registerIndicator(ParabolicSAR, CONFIG)
